import React, { memo, useCallback, useMemo } from 'react';
import { Sequence, useCurrentFrame, useVideoConfig } from 'remotion';
import { InnerLayer } from '../items/inner-layer';
import { LayerErrorBoundary } from '../items/layer';
import { calculateAnimationsStyle } from '../items/animation/calculate-animation';
import type { EditorStarterItem } from '../items/item-type';
import { deleteItems } from '../state/actions/delete-items';
import { useLinkingEnabled, useWriteContext } from '../utils/use-context';
import {
  computeGroupBBox,
  toGroupRelativeItem,
  type GroupUnit,
} from './utils/group-units';

interface GroupLayerProps {
  group: GroupUnit;
}

// Sequence 래퍼가 stacking box 를 만들지 않게 해, 내부 컨테이너의 zIndex 가
// per-track 레이어와 같은 stacking context 에서 경쟁하도록 한다(Layer.tsx 답습).
const sequenceStyle: React.CSSProperties = { display: 'contents' };

/**
 * 그룹 멤버 1개를 렌더한다. 기존 Layer 와 동일하게 LayerErrorBoundary 로 감싸
 * 멤버 하나의 런타임 에러가 그룹 전체/렌더 파이프라인으로 번지지 않게 한다.
 * (Layer 와 달리 per-item Sequence 는 쓰지 않는다 — 타이밍/변형은 GroupLayer 가 제어.)
 */
const GroupMemberLayer: React.FC<{ item: EditorStarterItem }> = ({ item }) => {
  const { setState } = useWriteContext();
  const isLinkingEnabled = useLinkingEnabled();
  const handleDeleteItem = useCallback(() => {
    setState({
      update: (state) => deleteItems(state, [item.id], { isLinkingEnabled }),
      commitToUndoStack: true,
    });
  }, [item.id, setState, isLinkingEnabled]);

  return (
    <LayerErrorBoundary
      itemId={item.id}
      onDeleteItem={handleDeleteItem}
    >
      {/* zIndex 미전달 → 컨테이너 내부에서는 DOM 순서(배경→전경)로 stacking. */}
      <InnerLayer
        item={item}
        trackMuted={false}
        cropBackground={false}
      />
    </LayerErrorBoundary>
  );
};

/**
 * 그룹 변형을 한 번 적용하고 멤버를 렌더하는 내부 컴포넌트.
 * Sequence 안에서 렌더되므로 useCurrentFrame() 이 그룹 시작 기준 상대 프레임을 준다.
 */
const GroupContent: React.FC<{ group: GroupUnit; groupDuration: number }> = ({
  group,
  groupDuration,
}) => {
  const { fps } = useVideoConfig();
  const currentFrame = useCurrentFrame();
  const { members, zIndex } = group;

  const bbox = useMemo(() => computeGroupBBox(members), [members]);

  // 그룹 등장 애니: 멤버가 공유하는 animations 에서 읽는다(첫 비어있지 않은 멤버 기준).
  // remotion-gen 경로에서 같은 그룹 멤버는 모두 동일한 애니를 갖는다(applyEntranceAnimations 계약).
  const groupAnimations = useMemo(() => {
    for (const m of members) {
      if (
        'animations' in m &&
        Array.isArray(m.animations) &&
        m.animations.length > 0
      ) {
        return m.animations;
      }
    }
    return [];
  }, [members]);

  // 컨테이너에 그룹 변형 1회 적용 → 모든 멤버가 같은 거리·같은 중심으로 움직인다(블록).
  // translate% 는 컨테이너(=멤버 union bbox) 크기 기준이라, 카드가 항상 bbox 를 채우므로
  // 카드 전체가 한 단위로 이동한다(per-item 의 "아이템 자기 크기 %" 와 의도적으로 다름).
  const animationStyle = useMemo(() => {
    if (groupAnimations.length === 0) return {};
    return calculateAnimationsStyle({
      animations: groupAnimations,
      currentFrame,
      framesPerSecond: fps,
      totalDurationInFrames: groupDuration,
    });
  }, [groupAnimations, currentFrame, fps, groupDuration]);

  // 멤버를 bbox 상대좌표 + animations 제거로 복제(원본 불변).
  const relativeMembers = useMemo(
    () => members.map((m) => toGroupRelativeItem(m, bbox)),
    [members, bbox],
  );

  const containerStyle: React.CSSProperties = useMemo(() => {
    const base: React.CSSProperties = {
      position: 'absolute',
      left: bbox.left,
      top: bbox.top,
      width: bbox.width,
      height: bbox.height,
      transformOrigin: 'center',
      zIndex,
    };
    if (animationStyle.transform) base.transform = animationStyle.transform;
    if (animationStyle.opacity !== undefined) {
      base.opacity = animationStyle.opacity;
    }
    if (animationStyle.clipPath) {
      base.clipPath = animationStyle.clipPath;
    }
    return base;
  }, [bbox, zIndex, animationStyle]);

  return (
    <div style={containerStyle}>
      {relativeMembers.map((m) => (
        <GroupMemberLayer
          key={m.id}
          item={m}
        />
      ))}
    </div>
  );
};

/**
 * 같은 groupId 멤버를 한 덩어리로 묶어 렌더한다.
 * - 멤버 union bbox 크기의 컨테이너에 그룹 등장 애니를 1회 적용(translate% 는 bbox 기준,
 *   scale/rotate 는 bbox 중심) → 카드 전체가 한 블록처럼 등장한다.
 * - 멤버는 bbox 상대좌표로 배치하고 개별 애니는 제거한다.
 */
const GroupLayerUnmemoized: React.FC<GroupLayerProps> = ({ group }) => {
  const { members } = group;
  const groupFrom = useMemo(
    () => Math.min(...members.map((m) => m.from)),
    [members],
  );
  const groupDuration = useMemo(
    () =>
      Math.max(...members.map((m) => m.from + m.durationInFrames)) - groupFrom,
    [members, groupFrom],
  );

  return (
    <Sequence
      from={groupFrom}
      durationInFrames={Math.max(1, groupDuration)}
      style={sequenceStyle}
    >
      <GroupContent
        group={group}
        groupDuration={Math.max(1, groupDuration)}
      />
    </Sequence>
  );
};

export const GroupLayer = memo(GroupLayerUnmemoized);
